"use client";

import React from "react";
import { v3, inkAccent } from "@/lib/theme";
import { fmtEur } from "@/lib/data/mock";
import { V3SectionHead } from "./V3SectionHead";
import { motion } from "framer-motion";

function V3Slider({ value, min, max, step = 1, onChange }: any) {
  const pct = ((value - min) / (max - min)) * 100;
  const ink = inkAccent();
  return (
    <div style={{ position: "relative", paddingTop: 6 }}>
      {/* Track background */}
      <div style={{ position: "absolute", left: 0, right: 0, top: 16, height: 6, borderRadius: 99, background: v3.line2 }} />
      {/* Filled portion */}
      <div style={{ position: "absolute", left: 0, top: 16, height: 6, width: `${pct}%`, borderRadius: 99, background: ink, boxShadow: v3.intense ? `0 0 12px ${v3.accentDim}` : "none" }} />
      <input
        type="range"
        className="w-full h-[6px] appearance-none bg-transparent cursor-pointer relative z-10 
        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[20px] [&::-webkit-slider-thumb]:h-[20px] 
        [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white 
        [&::-webkit-slider-thumb]:shadow-[0_0_8px_rgba(0,0,0,0.5)]"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

// Monthly annuity payment.
function annuity(principal: number, annualRatePct: number, years = 30) {
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

// Custom multi-line chart for the calculator.
function V3CalcChart({ series, kaufpreis, kredit }: any) {
  const W = 920, H = 260, pad = 4;
  const yMax = Math.max(...series.value, kaufpreis * 1.05);
  const yMin = Math.min(0, ...series.cumCf, -kredit * 0.1);
  const range = yMax - yMin || 1;
  const x = (i: number) => (i / (series.months.length - 1)) * (W - pad * 2) + pad;
  const y = (v: number) => H - pad - ((v - yMin) / range) * (H - pad * 2);
  const zeroY = y(0);
  const ink = inkAccent();

  const pathFrom = (arr: number[]) => arr.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: "block", overflow: "visible" }}>
      {/* Grid */}
      <line x1={0} x2={W} y1={zeroY} y2={zeroY} stroke={v3.line2} strokeWidth="1" strokeDasharray="2 3" />
      {[0.25, 0.5, 0.75].map((p, i) => {
        const yy = pad + p * (H - pad * 2);
        return <line key={i} x1={0} x2={W} y1={yy} y2={yy} stroke={v3.line} strokeWidth="0.5" />;
      })}

      {/* Marktwert area fill */}
      <path d={`${pathFrom(series.value)} L${x(series.value.length - 1)} ${H} L${x(0)} ${H} Z`} fill={v3.intense ? v3.accentBg : "rgba(250,250,250,0.04)"} />
      {/* Marktwert line */}
      <path d={pathFrom(series.value)} fill="none" stroke={ink} strokeWidth="2" strokeLinejoin="round" />
      {/* Restschuld line */}
      <path d={pathFrom(series.debt)} fill="none" stroke={v3.fg2} strokeWidth="1.5" strokeLinejoin="round" opacity="0.7" />
      {/* Cashflow cumulative line (dashed) */}
      <path d={pathFrom(series.cumCf)} fill="none" stroke={v3.dim} strokeWidth="1.5" strokeDasharray="4 3" strokeLinejoin="round" />

      {/* End-of-series labels */}
      <circle cx={x(series.value.length - 1)} cy={y(series.value[series.value.length - 1])} r="3.5" fill={ink} />
      <circle cx={x(series.debt.length - 1)} cy={y(series.debt[series.debt.length - 1])} r="3" fill={v3.fg2} />
      <circle cx={x(series.cumCf.length - 1)} cy={y(series.cumCf[series.cumCf.length - 1])} r="3" fill={v3.dim} />
    </svg>
  );
}

export function V3SectionCalculator() {
  const [kaufpreis, setKaufpreis] = React.useState(285000);
  const [ekPct, setEkPct]         = React.useState(20);
  const [zins, setZins]           = React.useState(3.4);

  // Assumptions stated visibly so the user trusts the math.
  const GROSS_YIELD = 4.2;      // % of Kaufpreis per year — typical Wiener Altbau
  const BEWIRT_PCT  = 12;       // % of Miete for Betriebskosten + Rücklagen
  const VALUE_GROWTH = 1.8;     // % p.a. value appreciation
  const RENT_GROWTH  = 1.5;     // % p.a. rent increase (VPI-linked, cautious)

  const kredit   = kaufpreis * (1 - ekPct / 100);
  const rate     = annuity(kredit, zins, 30);
  const miete0   = (kaufpreis * GROSS_YIELD / 100) / 12;
  const bewirt0  = miete0 * BEWIRT_PCT / 100;
  const cf0      = miete0 - bewirt0 - rate;

  // 20-year cumulative cashflow + value appreciation.
  const series = React.useMemo(() => {
    const out = { months: [] as number[], cumCf: [] as number[], value: [] as number[], debt: [] as number[] };
    let cum = 0;
    let val = kaufpreis;
    let debt = kredit;
    const rMonthly = zins / 100 / 12;
    const miete = (m: number) => miete0 * Math.pow(1 + RENT_GROWTH / 100, m / 12);
    const bewirt = (m: number) => miete(m) * BEWIRT_PCT / 100;
    for (let m = 0; m <= 240; m++) {
      const monthCf = miete(m) - bewirt(m) - rate;
      cum += monthCf;
      // Pay down debt with the principal portion of the rate.
      const interest = debt * rMonthly;
      const principalPaid = Math.max(0, rate - interest);
      debt = Math.max(0, debt - principalPaid);
      // Appreciation compounds monthly.
      val *= Math.pow(1 + VALUE_GROWTH / 100, 1 / 12);
      if (m % 6 === 0) {
        out.months.push(m);
        out.cumCf.push(cum);
        out.value.push(val);
        out.debt.push(debt);
      }
    }
    return out;
  }, [kaufpreis, ekPct, zins, kredit, rate, miete0]);

  const valEnd  = series.value[series.value.length - 1];
  const debtEnd = series.debt[series.debt.length - 1];
  const cumEnd  = series.cumCf[series.cumCf.length - 1];
  const equityEnd = valEnd - debtEnd;
  const total20  = equityEnd + cumEnd - (kaufpreis * ekPct / 100);

  const ink = inkAccent();

  return (
    <motion.section 
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{ padding: "120px 56px" }} className="rp-section"
    >
      <V3SectionHead n={2} kicker="Rechne bevor du kaufst"
        title="Was kostet dich ein Objekt"
        dimWord="wirklich?"
        sub="Zieh an den Reglern. Cashflow, Vermögensaufbau und Restschuld werden live über 20 Jahre neu berechnet — mit realistischen österreichischen Annahmen." />

      <div className="rp-stack" style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 32, alignItems: "start" }}>
        {/* Sliders + result card */}
        <div style={{ background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 16, padding: 28 }}>
          <div style={{ font: `12px ${v3.sans}`, color: v3.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 24 }}>Annahmen</div>

          {[
            { label: "Kaufpreis",   value: kaufpreis, min: 150000, max: 600000, step: 5000,  setter: setKaufpreis, format: (v: number) => fmtEur(v) },
            { label: "Eigenkapital", value: ekPct,    min: 10,     max: 50,     step: 1,     setter: setEkPct,     format: (v: number) => `${v} %` },
            { label: "Zinssatz",     value: zins,     min: 1.5,    max: 5.5,    step: 0.1,   setter: setZins,      format: (v: number) => `${v.toFixed(1).replace(".", ",")} %` },
          ].map((s, i) => (
            <div key={i} style={{ paddingTop: i > 0 ? 22 : 0, paddingBottom: 18, borderTop: i > 0 ? `1px solid ${v3.line}` : 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <span style={{ font: `13px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>{s.label}</span>
                <span style={{ font: `15px ${v3.mono}`, color: ink }}>{s.format(s.value)}</span>
              </div>
              <V3Slider value={s.value} min={s.min} max={s.max} step={s.step} onChange={s.setter} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, font: `10px ${v3.mono}`, color: v3.dim2 }}>
                <span>{s.format(s.min)}</span>
                <span>{s.format(s.max)}</span>
              </div>
            </div>
          ))}

          <div style={{ marginTop: 16, padding: 18, background: v3.intense ? v3.accentBg : v3.surf2, border: `1px solid ${v3.intense ? v3.accentDim : v3.line}`, borderRadius: 12 }}>
            <div style={{ font: `11px ${v3.sans}`, color: v3.accent, textTransform: "uppercase", letterSpacing: "0.08em" }}>Nach 20 Jahren</div>
            <div style={{ font: `500 38px ${v3.sans}`, color: v3.fg, marginTop: 6, letterSpacing: "-0.025em" }}>{fmtEur(total20, { sign: true })}</div>
            <div style={{ font: `12px ${v3.sans}`, color: v3.dim, marginTop: 4 }}>Netto-Vermögensaufbau · EK + Cashflow + Wertsteigerung</div>
          </div>
        </div>

        {/* Chart + KPI strip */}
        <div style={{ background: v3.surf, border: `1px solid ${v3.line}`, borderRadius: 16, padding: 28, overflowX: "auto" }}>
          {/* Top KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 0, borderBottom: `1px solid ${v3.line}`, paddingBottom: 18, marginBottom: 18, minWidth: 600 }}>
            {[
              ["Kredithöhe",      fmtEur(kredit)],
              ["Monatsrate",      fmtEur(rate, { decimals: 0 })],
              ["Mietertrag (Start)", fmtEur(miete0, { decimals: 0 })],
              ["Cashflow (Start)", fmtEur(cf0, { decimals: 0, sign: true })],
            ].map((k, i) => (
              <div key={i} style={{ padding: "0 20px", borderLeft: i > 0 ? `1px solid ${v3.line}` : 0 }}>
                <div style={{ font: `11px ${v3.sans}`, color: v3.dim, textTransform: "uppercase", letterSpacing: "0.06em" }}>{k[0]}</div>
                <div style={{ font: `500 22px ${v3.sans}`, color: i === 3 ? (cf0 >= 0 ? ink : v3.dim) : v3.fg, marginTop: 6, letterSpacing: "-0.02em" }}>{k[1]}</div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div style={{ position: "relative", minWidth: 600 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
              <div style={{ font: `15px ${v3.sans}`, color: v3.fg, fontWeight: 500 }}>Vermögensaufbau · 20 Jahre</div>
              <div style={{ display: "flex", gap: 16, font: `11px ${v3.mono}`, color: v3.dim, flexWrap: "wrap" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 2, background: ink }} /> Marktwert</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 2, background: v3.fg2, opacity: 0.7 }} /> Restschuld</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 2, background: v3.dim, borderTop: `2px dashed ${v3.dim}` }} /> Σ Cashflow</span>
              </div>
            </div>
            <V3CalcChart series={series} kaufpreis={kaufpreis} kredit={kredit} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, font: `11px ${v3.mono}`, color: v3.dim2 }}>
              <span>Jahr 0</span><span>Jahr 5</span><span>Jahr 10</span><span>Jahr 15</span><span>Jahr 20</span>
            </div>
          </div>

          {/* Bottom summary */}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${v3.line}`, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, minWidth: 600 }}>
            {[
              ["Marktwert nach 20J", fmtEur(valEnd), `${VALUE_GROWTH.toFixed(1).replace(".", ",")} % p.a. Wertsteigerung`],
              ["Restschuld nach 20J", fmtEur(debtEnd), "bei 30-jähriger Annuität"],
              ["Σ Cashflow 20J", fmtEur(cumEnd, { sign: true }), `${RENT_GROWTH.toFixed(1).replace(".", ",")} % p.a. Mietsteigerung`],
            ].map((k, i) => (
              <div key={i}>
                <div style={{ font: `11px ${v3.sans}`, color: v3.dim, textTransform: "uppercase", letterSpacing: "0.06em" }}>{k[0]}</div>
                <div style={{ font: `500 22px ${v3.sans}`, color: i === 2 ? (cumEnd >= 0 ? ink : v3.dim) : v3.fg, marginTop: 4, letterSpacing: "-0.02em" }}>{k[1]}</div>
                <div style={{ font: `11px ${v3.mono}`, color: v3.dim2, marginTop: 2 }}>{k[2]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}
